'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { motion, useAnimation, type SVGMotionProps, type UseInViewOptions, type LegacyAnimationControls, type Variants, type HTMLMotionProps } from 'motion/react';
import { cn } from '@/lib/utils';
import { useIsInView } from '@/hooks/use-is-in-view';
import { Slot, type WithAsChild } from '@/components/animate-ui/primitives/animate/slot';
const staticAnimations = {
  path: {
    initial: {
      pathLength: 1
    },
    animate: {
      pathLength: [0.05, 1],
      transition: {
        duration: 0.8,
        ease: 'easeInOut'
      }
    }
  } as Variants,
  'path-loop': {
    initial: {
      pathLength: 1
    },
    animate: {
      pathLength: [1, 0.05, 1],
      transition: {
        duration: 1.6,
        ease: 'easeInOut'
      }
    }
  } as Variants
} as const;
type StaticAnimations = keyof typeof staticAnimations;
type TriggerProp<T = string> = boolean | StaticAnimations | T;
type Trigger = TriggerProp<string>;
type AnimateIconContextValue = {
  controls: LegacyAnimationControls | undefined;
  animation: StaticAnimations | string;
  loop: boolean;
  loopDelay: number;
  active: boolean;
  animate?: Trigger;
  initialOnAnimateEnd?: boolean;
  completeOnStop?: boolean;
  persistOnAnimateEnd?: boolean;
  delay?: number;
};
type DefaultIconProps<T = string> = {
  animate?: TriggerProp<T>;
  animateOnHover?: TriggerProp<T>;
  animateOnTap?: TriggerProp<T>;
  animateOnView?: TriggerProp<T>;
  animateOnViewMargin?: UseInViewOptions['margin'];
  animateOnViewOnce?: boolean;
  animation?: T | StaticAnimations;
  loop?: boolean;
  loopDelay?: number;
  initialOnAnimateEnd?: boolean;
  completeOnStop?: boolean;
  persistOnAnimateEnd?: boolean;
  delay?: number;
};
type AnimateIconProps<T = string> = WithAsChild<HTMLMotionProps<'span'> & DefaultIconProps<T> & {
  children: React.ReactNode;
  asChild?: boolean;
}>;
type IconProps<T> = DefaultIconProps<T> & Omit<SVGMotionProps<SVGSVGElement>, 'animate'> & {
  size?: number;
};
type IconWrapperProps<T> = IconProps<T> & {
  icon: React.ComponentType<IconProps<T>>;
};
const AnimateIconContext = React.createContext<AnimateIconContextValue | null>(null);
function useAnimateIconContext() {
  const $ = _c(1);
  const context = React.useContext(AnimateIconContext);
  if (!context) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {
        controls: undefined,
        animation: "default",
        loop: undefined,
        loopDelay: undefined,
        active: undefined,
        animate: undefined,
        initialOnAnimateEnd: undefined,
        completeOnStop: undefined,
        persistOnAnimateEnd: undefined,
        delay: undefined
      };
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  return context;
}
function composeEventHandlers<E extends React.SyntheticEvent<unknown>>(theirs?: (event: E) => void, ours?: (event: E) => void) {
  return (event: E) => {
    theirs?.(event);
    ours?.(event);
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyProps = Record<string, any>;
function AnimateIcon({
  asChild = false,
  animate = false,
  animateOnHover = false,
  animateOnTap = false,
  animateOnView = false,
  animateOnViewMargin = '0px',
  animateOnViewOnce = true,
  animation = 'default',
  loop = false,
  loopDelay = 0,
  initialOnAnimateEnd = false,
  completeOnStop = false,
  persistOnAnimateEnd = false,
  delay = 0,
  children,
  ...props
}: AnimateIconProps) {
  const controls = useAnimation();
  const [localAnimate, setLocalAnimate] = React.useState<boolean>(() => {
    if (animate === undefined || animate === false) return false;
    return delay <= 0;
  });
  const [currentAnimation, setCurrentAnimation] = React.useState<string | StaticAnimations>(typeof animate === 'string' ? animate : animation);
  const [status, setStatus] = React.useState<'initial' | 'animate'>('initial');
  const delayRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const loopDelayRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAnimateInProgressRef = React.useRef<boolean>(false);
  const animateEndPromiseRef = React.useRef<Promise<void> | null>(null);
  const resolveAnimateEndRef = React.useRef<(() => void) | null>(null);
  const activeRef = React.useRef<boolean>(localAnimate);
  const runGenRef = React.useRef(0);
  const cancelledRef = React.useRef(false);
  const bumpGeneration = React.useCallback(() => {
    runGenRef.current++;
  }, []);
  const startAnimation = React.useCallback((trigger: TriggerProp) => {
    const next = typeof trigger === 'string' ? trigger : animation;
    bumpGeneration();
    if (delayRef.current) {
      clearTimeout(delayRef.current);
      delayRef.current = null;
    }
    setCurrentAnimation(next);
    if (delay > 0) {
      setLocalAnimate(false);
      delayRef.current = setTimeout(() => {
        setLocalAnimate(true);
      }, delay);
    } else {
      setLocalAnimate(true);
    }
  }, [animation, delay, bumpGeneration]);
  const stopAnimation = React.useCallback(() => {
    bumpGeneration();
    if (delayRef.current) {
      clearTimeout(delayRef.current);
      delayRef.current = null;
    }
    if (loopDelayRef.current) {
      clearTimeout(loopDelayRef.current);
      loopDelayRef.current = null;
    }
    setLocalAnimate(false);
  }, [bumpGeneration]);
  React.useEffect(() => {
    activeRef.current = localAnimate;
  }, [localAnimate]);
  React.useEffect(() => {
    if (animate === undefined) return;
    setCurrentAnimation(typeof animate === 'string' ? animate : animation);
    if (animate) startAnimation(animate as TriggerProp);else stopAnimation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animate]);
  React.useEffect(() => {
    return () => {
      if (delayRef.current) clearTimeout(delayRef.current);
      if (loopDelayRef.current) clearTimeout(loopDelayRef.current);
    };
  }, []);
  const viewOuterRef = React.useRef<HTMLElement>(null);
  const {
    ref: inViewRef,
    isInView
  } = useIsInView(viewOuterRef, {
    inView: !!animateOnView,
    inViewOnce: animateOnViewOnce,
    inViewMargin: animateOnViewMargin
  });
  const startAnim = React.useCallback(async (anim: 'initial' | 'animate', method: 'start' | 'set' = 'start') => {
    try {
      await controls[method](anim);
      setStatus(anim);
    } catch {
      return;
    }
  }, [controls]);
  React.useEffect(() => {
    if (!animateOnView) return;
    if (isInView) startAnimation(animateOnView);else stopAnimation();
  }, [isInView, animateOnView, startAnimation, stopAnimation]);
  React.useEffect(() => {
    const gen = ++runGenRef.current;
    cancelledRef.current = false;
    async function run() {
      if (cancelledRef.current || gen !== runGenRef.current) {
        await startAnim('initial');
        return;
      }
      if (!localAnimate) {
        if (completeOnStop && isAnimateInProgressRef.current && animateEndPromiseRef.current) {
          try {
            await animateEndPromiseRef.current;
          } catch {
            // noop
          }
        }
        if (!persistOnAnimateEnd) {
          if (cancelledRef.current || gen !== runGenRef.current) {
            await startAnim('initial');
            return;
          }
          await startAnim('initial');
        }
        return;
      }
      if (loop) {
        if (cancelledRef.current || gen !== runGenRef.current) {
          await startAnim('initial');
          return;
        }
        await startAnim('initial', 'set');
      }
      isAnimateInProgressRef.current = true;
      animateEndPromiseRef.current = new Promise<void>(resolve => {
        resolveAnimateEndRef.current = resolve;
      });
      if (cancelledRef.current || gen !== runGenRef.current) {
        isAnimateInProgressRef.current = false;
        resolveAnimateEndRef.current?.();
        resolveAnimateEndRef.current = null;
        animateEndPromiseRef.current = null;
        await startAnim('initial');
        return;
      }
      await startAnim('animate');
      if (cancelledRef.current || gen !== runGenRef.current) {
        isAnimateInProgressRef.current = false;
        resolveAnimateEndRef.current?.();
        resolveAnimateEndRef.current = null;
        animateEndPromiseRef.current = null;
        await startAnim('initial');
        return;
      }
      isAnimateInProgressRef.current = false;
      resolveAnimateEndRef.current?.();
      resolveAnimateEndRef.current = null;
      animateEndPromiseRef.current = null;
      if (initialOnAnimateEnd) {
        if (cancelledRef.current || gen !== runGenRef.current) {
          await startAnim('initial');
          return;
        }
        await startAnim('initial', 'set');
      }
      if (loop) {
        if (loopDelay > 0) {
          await new Promise<void>(resolve => {
            loopDelayRef.current = setTimeout(() => {
              loopDelayRef.current = null;
              resolve();
            }, loopDelay);
          });
          if (cancelledRef.current || gen !== runGenRef.current) {
            await startAnim('initial');
            return;
          }
          if (!activeRef.current) {
            if (status !== 'initial' && !persistOnAnimateEnd) await startAnim('initial');
            return;
          }
        } else {
          if (!activeRef.current) {
            if (status !== 'initial' && !persistOnAnimateEnd) await startAnim('initial');
            return;
          }
        }
        if (cancelledRef.current || gen !== runGenRef.current) {
          await startAnim('initial');
          return;
        }
        await run();
      }
    }
    void run();
    return () => {
      cancelledRef.current = true;
      if (delayRef.current) {
        clearTimeout(delayRef.current);
        delayRef.current = null;
      }
      if (loopDelayRef.current) {
        clearTimeout(loopDelayRef.current);
        loopDelayRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localAnimate, controls]);
  const childProps = (React.isValidElement(children) ? (children as React.ReactElement).props : {}) as AnyProps;
  const handleMouseEnter = composeEventHandlers<React.MouseEvent<HTMLElement>>(childProps.onMouseEnter, () => {
    if (animateOnHover) startAnimation(animateOnHover);
  });
  const handleMouseLeave = composeEventHandlers<React.MouseEvent<HTMLElement>>(childProps.onMouseLeave, () => {
    if (animateOnHover || animateOnTap) stopAnimation();
  });
  const handlePointerDown = composeEventHandlers<React.PointerEvent<HTMLElement>>(childProps.onPointerDown, () => {
    if (animateOnTap) startAnimation(animateOnTap);
  });
  const handlePointerUp = composeEventHandlers<React.PointerEvent<HTMLElement>>(childProps.onPointerUp, () => {
    if (animateOnTap) stopAnimation();
  });
  const content = asChild ? <Slot ref={inViewRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} {...props}>
      {children}
    </Slot> : <motion.span ref={inViewRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} {...props}>
      {children}
    </motion.span>;
  return <AnimateIconContext.Provider value={{
    controls,
    animation: currentAnimation,
    loop,
    loopDelay,
    active: localAnimate,
    animate,
    initialOnAnimateEnd,
    completeOnStop,
    delay
  }}>
      {content}
    </AnimateIconContext.Provider>;
}
const pathClassName = "[&_[stroke-dasharray='1px_1px']]:![stroke-dasharray:1px_0px]";
function IconWrapper(t0) {
  const $ = _c(91);
  let IconComponent;
  let animate;
  let animateOnHover;
  let animateOnTap;
  let animateOnView;
  let animateOnViewMargin;
  let animateOnViewOnce;
  let animationProp;
  let className;
  let completeOnStop;
  let delay;
  let initialOnAnimateEnd;
  let loop;
  let loopDelay;
  let persistOnAnimateEnd;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      size: t1,
      animation: animationProp,
      animate,
      animateOnHover,
      animateOnTap,
      animateOnView,
      animateOnViewMargin,
      animateOnViewOnce,
      icon: IconComponent,
      loop,
      loopDelay,
      persistOnAnimateEnd,
      initialOnAnimateEnd,
      delay,
      completeOnStop,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = IconComponent;
    $[2] = animate;
    $[3] = animateOnHover;
    $[4] = animateOnTap;
    $[5] = animateOnView;
    $[6] = animateOnViewMargin;
    $[7] = animateOnViewOnce;
    $[8] = animationProp;
    $[9] = className;
    $[10] = completeOnStop;
    $[11] = delay;
    $[12] = initialOnAnimateEnd;
    $[13] = loop;
    $[14] = loopDelay;
    $[15] = persistOnAnimateEnd;
    $[16] = props;
    $[17] = t1;
  } else {
    IconComponent = $[1];
    animate = $[2];
    animateOnHover = $[3];
    animateOnTap = $[4];
    animateOnView = $[5];
    animateOnViewMargin = $[6];
    animateOnViewOnce = $[7];
    animationProp = $[8];
    className = $[9];
    completeOnStop = $[10];
    delay = $[11];
    initialOnAnimateEnd = $[12];
    loop = $[13];
    loopDelay = $[14];
    persistOnAnimateEnd = $[15];
    props = $[16];
    t1 = $[17];
  }
  const size = t1 === undefined ? 28 : t1;
  const context = React.useContext(AnimateIconContext);
  if (context) {
    const {
      controls,
      animation: parentAnimation,
      loop: parentLoop,
      loopDelay: parentLoopDelay,
      active: parentActive,
      animate: parentAnimate,
      persistOnAnimateEnd: parentPersistOnAnimateEnd,
      initialOnAnimateEnd: parentInitialOnAnimateEnd,
      delay: parentDelay,
      completeOnStop: parentCompleteOnStop
    } = context;
    const hasOverrides = animate !== undefined || animateOnHover !== undefined || animateOnTap !== undefined || animateOnView !== undefined || loop !== undefined || loopDelay !== undefined || initialOnAnimateEnd !== undefined || persistOnAnimateEnd !== undefined || delay !== undefined || completeOnStop !== undefined;
    if (hasOverrides) {
      const inheritedAnimate = parentActive ? animationProp ?? parentAnimation ?? "default" : false;
      const finalAnimate = (animate ?? parentAnimate ?? inheritedAnimate) as Trigger;
      const t2 = animationProp ?? parentAnimation;
      const t3 = loop ?? parentLoop;
      const t4 = loopDelay ?? parentLoopDelay;
      const t5 = persistOnAnimateEnd ?? parentPersistOnAnimateEnd;
      const t6 = initialOnAnimateEnd ?? parentInitialOnAnimateEnd;
      const t7 = delay ?? parentDelay;
      const t8 = completeOnStop ?? parentCompleteOnStop;
      const t9 = ((animationProp ?? parentAnimation) === "path" || (animationProp ?? parentAnimation) === "path-loop") && pathClassName;
      let t10;
      if ($[18] !== className || $[19] !== t9) {
        t10 = cn(className, t9);
        $[18] = className;
        $[19] = t9;
        $[20] = t10;
      } else {
        t10 = $[20];
      }
      let t11;
      if ($[21] !== IconComponent || $[22] !== props || $[23] !== size || $[24] !== t10) {
        t11 = <IconComponent size={size} className={t10} {...props} />;
        $[21] = IconComponent;
        $[22] = props;
        $[23] = size;
        $[24] = t10;
        $[25] = t11;
      } else {
        t11 = $[25];
      }
      let t12;
      if ($[26] !== animateOnHover || $[27] !== animateOnTap || $[28] !== animateOnView || $[29] !== animateOnViewMargin || $[30] !== animateOnViewOnce || $[31] !== finalAnimate || $[32] !== t11 || $[33] !== t2 || $[34] !== t3 || $[35] !== t4 || $[36] !== t5 || $[37] !== t6 || $[38] !== t7 || $[39] !== t8) {
        t12 = <AnimateIcon animate={finalAnimate} animateOnHover={animateOnHover} animateOnTap={animateOnTap} animateOnView={animateOnView} animateOnViewMargin={animateOnViewMargin} animateOnViewOnce={animateOnViewOnce} animation={t2} loop={t3} loopDelay={t4} persistOnAnimateEnd={t5} initialOnAnimateEnd={t6} delay={t7} completeOnStop={t8} asChild={true}>{t11}</AnimateIcon>;
        $[26] = animateOnHover;
        $[27] = animateOnTap;
        $[28] = animateOnView;
        $[29] = animateOnViewMargin;
        $[30] = animateOnViewOnce;
        $[31] = finalAnimate;
        $[32] = t11;
        $[33] = t2;
        $[34] = t3;
        $[35] = t4;
        $[36] = t5;
        $[37] = t6;
        $[38] = t7;
        $[39] = t8;
        $[40] = t12;
      } else {
        t12 = $[40];
      }
      return t12;
    }
    const animationToUse = animationProp ?? parentAnimation;
    const loopToUse = parentLoop;
    const loopDelayToUse = parentLoopDelay;
    let t2;
    if ($[41] !== animationToUse || $[42] !== controls || $[43] !== loopDelayToUse || $[44] !== loopToUse || $[45] !== parentActive || $[46] !== parentAnimate || $[47] !== parentCompleteOnStop || $[48] !== parentDelay || $[49] !== parentInitialOnAnimateEnd) {
      t2 = {
        controls,
        animation: animationToUse,
        loop: loopToUse,
        loopDelay: loopDelayToUse,
        active: parentActive,
        animate: parentAnimate,
        initialOnAnimateEnd: parentInitialOnAnimateEnd,
        delay: parentDelay,
        completeOnStop: parentCompleteOnStop
      };
      $[41] = animationToUse;
      $[42] = controls;
      $[43] = loopDelayToUse;
      $[44] = loopToUse;
      $[45] = parentActive;
      $[46] = parentAnimate;
      $[47] = parentCompleteOnStop;
      $[48] = parentDelay;
      $[49] = parentInitialOnAnimateEnd;
      $[50] = t2;
    } else {
      t2 = $[50];
    }
    const t3 = (animationToUse === "path" || animationToUse === "path-loop") && pathClassName;
    let t4;
    if ($[51] !== className || $[52] !== t3) {
      t4 = cn(className, t3);
      $[51] = className;
      $[52] = t3;
      $[53] = t4;
    } else {
      t4 = $[53];
    }
    let t5;
    if ($[54] !== IconComponent || $[55] !== props || $[56] !== size || $[57] !== t4) {
      t5 = <IconComponent size={size} className={t4} {...props} />;
      $[54] = IconComponent;
      $[55] = props;
      $[56] = size;
      $[57] = t4;
      $[58] = t5;
    } else {
      t5 = $[58];
    }
    let t6;
    if ($[59] !== t2 || $[60] !== t5) {
      t6 = <AnimateIconContext.Provider value={t2}>{t5}</AnimateIconContext.Provider>;
      $[59] = t2;
      $[60] = t5;
      $[61] = t6;
    } else {
      t6 = $[61];
    }
    return t6;
  }
  if (animate !== undefined || animateOnHover !== undefined || animateOnTap !== undefined || animateOnView !== undefined || animationProp !== undefined) {
    const t2 = (animationProp === "path" || animationProp === "path-loop") && pathClassName;
    let t3;
    if ($[62] !== className || $[63] !== t2) {
      t3 = cn(className, t2);
      $[62] = className;
      $[63] = t2;
      $[64] = t3;
    } else {
      t3 = $[64];
    }
    let t4;
    if ($[65] !== IconComponent || $[66] !== props || $[67] !== size || $[68] !== t3) {
      t4 = <IconComponent size={size} className={t3} {...props} />;
      $[65] = IconComponent;
      $[66] = props;
      $[67] = size;
      $[68] = t3;
      $[69] = t4;
    } else {
      t4 = $[69];
    }
    let t5;
    if ($[70] !== animate || $[71] !== animateOnHover || $[72] !== animateOnTap || $[73] !== animateOnView || $[74] !== animateOnViewMargin || $[75] !== animateOnViewOnce || $[76] !== animationProp || $[77] !== completeOnStop || $[78] !== delay || $[79] !== loop || $[80] !== loopDelay || $[81] !== t4) {
      t5 = <AnimateIcon animate={animate} animateOnHover={animateOnHover} animateOnTap={animateOnTap} animateOnView={animateOnView} animateOnViewMargin={animateOnViewMargin} animateOnViewOnce={animateOnViewOnce} animation={animationProp} loop={loop} loopDelay={loopDelay} delay={delay} completeOnStop={completeOnStop} asChild={true}>{t4}</AnimateIcon>;
      $[70] = animate;
      $[71] = animateOnHover;
      $[72] = animateOnTap;
      $[73] = animateOnView;
      $[74] = animateOnViewMargin;
      $[75] = animateOnViewOnce;
      $[76] = animationProp;
      $[77] = completeOnStop;
      $[78] = delay;
      $[79] = loop;
      $[80] = loopDelay;
      $[81] = t4;
      $[82] = t5;
    } else {
      t5 = $[82];
    }
    return t5;
  }
  const t2 = (animationProp === "path" || animationProp === "path-loop") && pathClassName;
  let t3;
  if ($[83] !== className || $[84] !== t2) {
    t3 = cn(className, t2);
    $[83] = className;
    $[84] = t2;
    $[85] = t3;
  } else {
    t3 = $[85];
  }
  let t4;
  if ($[86] !== IconComponent || $[87] !== props || $[88] !== size || $[89] !== t3) {
    t4 = <IconComponent size={size} className={t3} {...props} />;
    $[86] = IconComponent;
    $[87] = props;
    $[88] = size;
    $[89] = t3;
    $[90] = t4;
  } else {
    t4 = $[90];
  }
  return t4;
}
function getVariants<V extends {
  default: T;
  [key: string]: T;
}, T extends Record<string, Variants>>(animations: V): T {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const {
    animation: animationType
  } = useAnimateIconContext();
  let result: T;
  if (animationType in staticAnimations) {
    const variant = staticAnimations[animationType as StaticAnimations];
    result = {} as T;
    for (const key in animations.default) {
      if ((animationType === 'path' || animationType === 'path-loop') && key.includes('group')) continue;
      result[key] = variant as T[Extract<keyof T, string>];
    }
  } else {
    result = animations[animationType as keyof V] as T ?? animations.default;
  }
  return result;
}
export { pathClassName, staticAnimations, AnimateIcon, IconWrapper, useAnimateIconContext, getVariants, type IconProps, type IconWrapperProps, type AnimateIconProps, type AnimateIconContextValue };